import { useState, useEffect, useCallback, useMemo } from "react";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getDealerStock, createMarketplaceOrder, getMyMarketplaceOrders, getDealerProductDetail, getDealerRepairStatus, requestStockNotification } from "@/lib/api";
import { toast } from "sonner";
import {
  ShoppingCart, Loader2, Search, Plus, Minus, Trash2, Package, Send,
  Clock, CheckCircle, XCircle, Calendar, ChevronRight, Lock, Tag, Shield, Info, Bell, FilterX,
} from "lucide-react";

const statusColors = {
  pending: "bg-amber-100 text-amber-700",
  processing: "bg-blue-100 text-blue-700",
  fulfilled: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

const REPAIR_CATEGORIES = ["device parts", "device part", "tools", "repair parts", "repair tools", "repair"];

function ProductDetailModal({ product, open, onClose, onAddToCart, onNotifyMe, repairApproved }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !product) return;
    setLoading(true);
    getDealerProductDetail(product.id)
      .then(setDetail)
      .catch(() => setDetail(product))
      .finally(() => setLoading(false));
  }, [open, product]);

  if (!open) return null;
  const p = detail || product;
  const isRepairProduct = REPAIR_CATEGORIES.some((rc) => (p?.category_name || "").toLowerCase().includes(rc));
  const restricted = isRepairProduct && !repairApproved;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="product-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">{p?.name || "Product Details"}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>
        ) : (
          <div className="space-y-4">
            {p?.image_url && (
              <div className="w-full h-56 bg-slate-50 rounded-lg overflow-hidden flex items-center justify-center">
                <img src={p.image_url} alt={p.name} className="w-full h-full object-contain" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <InfoRow label="SKU" value={p?.sku || "N/A"} testid="detail-sku" />
              <InfoRow label="UPC" value={p?.upc || "N/A"} testid="detail-upc" />
              <InfoRow label="Condition" value={p?.condition_name || p?.condition || "N/A"} testid="detail-condition" />
              <InfoRow label="Carrier" value={p?.carrier_name || p?.carrier_lock || "N/A"} testid="detail-carrier" />
              <InfoRow label="Category" value={p?.category_name || "N/A"} testid="detail-category" />
              <InfoRow label="Warranty" value={p?.warranty_period || "N/A"} testid="detail-warranty" />
              <InfoRow label="Retail Price" value={`$${p?.retail_price?.toFixed(2) || "0.00"}`} testid="detail-price" highlight />
              <InfoRow label="In Stock" value={`${p?.quantity_in_stock || 0} units`} testid="detail-stock" />
            </div>
            {p?.description && (
              <div data-testid="detail-description">
                <p className="text-xs text-slate-500 font-medium mb-1">Description</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-3">{p.description}</p>
              </div>
            )}
            {restricted ? (
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="repair-restricted-msg">
                <Lock className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <p className="text-sm text-amber-700">This is a repair product. Your location must be approved for Device Repair to order.</p>
              </div>
            ) : p?.quantity_in_stock === 0 ? (
              <Button
                className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => { onNotifyMe(p); }}
                disabled={p?.notify_requested}
                data-testid="detail-notify-me"
              >
                <Bell className="w-4 h-4 mr-2" />
                {p?.notify_requested ? "You'll be notified" : "Notify Me When Available"}
              </Button>
            ) : (
              <Button
                className="w-full bg-teal hover:bg-teal/90 text-white"
                onClick={() => { onAddToCart(p); onClose(); }}
                data-testid="detail-add-to-cart"
              >
                <Plus className="w-4 h-4 mr-2" /> Add to Cart
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ label, value, testid, highlight }) {
  return (
    <div data-testid={testid}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`font-medium ${highlight ? "text-teal text-lg" : "text-navy"}`}>{value}</p>
    </div>
  );
}

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 text-xs bg-white" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function DealerMarketplace() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("browse");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [subcategoryFilter, setSubcategoryFilter] = useState("all");
  const [itemGroupFilter, setItemGroupFilter] = useState("all");
  const [brandFilter, setBrandFilter] = useState("all");
  const [conditionFilter, setConditionFilter] = useState("all");
  const [carrierFilter, setCarrierFilter] = useState("all");
  const [compatModelFilter, setCompatModelFilter] = useState("all");
  // Active variant per group (key = variant_group_id, value = product.id within that group)
  const [activeVariantByGroup, setActiveVariantByGroup] = useState({});
  const [orderSearch, setOrderSearch] = useState("");
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [repairApproved, setRepairApproved] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [productsData, ordersData, repairData] = await Promise.all([
        getDealerStock(),
        getMyMarketplaceOrders(),
        getDealerRepairStatus(),
      ]);
      setProducts(productsData);
      setOrders(ordersData);
      setRepairApproved(repairData.best_repair_status === "approved");
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filterOptions = useMemo(() => {
    const uniq = (arr) => Array.from(new Set(arr.filter(Boolean))).sort();
    const categories = uniq(products.map((p) => p.category_name));
    // Subcategories scoped by selected category when applicable
    const subPool = categoryFilter === "all"
      ? products
      : products.filter((p) => (p.category_name || "") === categoryFilter);
    const subcategories = uniq(subPool.map((p) => p.subcategory_name));
    const itemGroups = uniq(products.map((p) => p.item_group_name));
    const brands = uniq(products.map((p) => p.brand_name));
    const conditions = uniq(products.map((p) => p.condition_name));
    const carriers = uniq(products.map((p) => p.carrier_name));
    const compatModels = uniq(products.flatMap((p) => p.compatible_model_names || []));
    return { categories, subcategories, itemGroups, brands, conditions, carriers, compatModels };
  }, [products, categoryFilter]);

  // Reset sub-category when category changes if no longer valid
  useEffect(() => {
    if (subcategoryFilter !== "all" && !filterOptions.subcategories.includes(subcategoryFilter)) {
      setSubcategoryFilter("all");
    }
  }, [filterOptions.subcategories, subcategoryFilter]);

  const filteredProducts = products.filter((p) => {
    const matchesSearch = search ? (p.name?.toLowerCase().includes(search.toLowerCase()) || p.sku?.toLowerCase().includes(search.toLowerCase())) : true;
    const matchesCat = categoryFilter === "all" || (p.category_name || "") === categoryFilter;
    const matchesSub = subcategoryFilter === "all" || (p.subcategory_name || "") === subcategoryFilter;
    const matchesGroup = itemGroupFilter === "all" || (p.item_group_name || "") === itemGroupFilter;
    const matchesBrand = brandFilter === "all" || (p.brand_name || "") === brandFilter;
    const matchesCond = conditionFilter === "all" || (p.condition_name || "") === conditionFilter;
    const matchesCarrier = carrierFilter === "all" || (p.carrier_name || "") === carrierFilter;
    const matchesModel = compatModelFilter === "all" || (p.compatible_model_names || []).includes(compatModelFilter);
    return matchesSearch && matchesCat && matchesSub && matchesGroup && matchesBrand && matchesCond && matchesCarrier && matchesModel;
  });

  // Group products by variant_group_id. Each group becomes a single card; ungrouped products
  // (no variant_group_id) are their own "group of 1".
  const productGroups = useMemo(() => {
    const groupMap = new Map();
    const seen = new Set();
    const out = [];
    for (const p of filteredProducts) {
      const gid = p.variant_group_id;
      if (gid) {
        if (!groupMap.has(gid)) {
          const arr = [];
          groupMap.set(gid, arr);
          out.push({ key: `g-${gid}`, group_id: gid, variants: arr });
        }
        groupMap.get(gid).push(p);
      } else {
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        out.push({ key: `p-${p.id}`, group_id: null, variants: [p] });
      }
    }
    return out;
  }, [filteredProducts]);

  const activeFilterCount = [categoryFilter, subcategoryFilter, itemGroupFilter, brandFilter, conditionFilter, carrierFilter, compatModelFilter].filter((f) => f !== "all").length;

  const clearFilters = () => {
    setCategoryFilter("all");
    setSubcategoryFilter("all");
    setItemGroupFilter("all");
    setBrandFilter("all");
    setConditionFilter("all");
    setCarrierFilter("all");
    setCompatModelFilter("all");
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const matchesSearch = orderSearch ? o.order_number?.toLowerCase().includes(orderSearch.toLowerCase()) : true;
      const orderDate = new Date(o.created_at);
      const matchesStart = dateStart ? orderDate >= new Date(dateStart) : true;
      const matchesEnd = dateEnd ? orderDate <= new Date(dateEnd + "T23:59:59") : true;
      return matchesSearch && matchesStart && matchesEnd;
    });
  }, [orders, orderSearch, dateStart, dateEnd]);

  const isRepairProduct = (product) => {
    return REPAIR_CATEGORIES.some((rc) => (product.category_name || "").toLowerCase().includes(rc));
  };

  const addToCart = (product) => {
    if (isRepairProduct(product) && !repairApproved) {
      toast.error("Your location must be approved for Device Repair to order this product.");
      return;
    }
    const existing = cart.find((c) => c.product_id === product.id);
    if (existing) {
      if (existing.quantity >= product.quantity_in_stock) return toast.error("Max stock reached");
      setCart(cart.map((c) => c.product_id === product.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { product_id: product.id, product_name: product.name, quantity: 1, unit_price: product.retail_price, max_stock: product.quantity_in_stock }]);
    }
    toast.success(`Added ${product.name} to cart`);
  };

  const updateQty = (idx, delta) => { setCart(cart.map((c, i) => i === idx ? { ...c, quantity: Math.max(1, Math.min(c.quantity + delta, c.max_stock)) } : c)); };
  const removeFromCart = (idx) => { setCart(cart.filter((_, i) => i !== idx)); };

  const handleNotifyMe = async (product) => {
    try {
      const res = await requestStockNotification(product.id);
      toast.success(res.already ? "You're already on the notify list for this product" : `We'll email you when ${product.name} is back in stock`);
      setProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, notify_requested: true } : p));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to subscribe to notifications");
    }
  };

  const submitOrder = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    setSubmitting(true);
    try {
      await createMarketplaceOrder({ items: cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity })), notes });
      toast.success("Order placed!");
      setCart([]);
      setNotes("");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Order failed"); }
    finally { setSubmitting(false); }
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.quantity * c.unit_price, 0);
  const statusIcon = (s) => { const m = { pending: Clock, processing: Package, fulfilled: CheckCircle, cancelled: XCircle }; const I = m[s] || Clock; return <I className="w-3.5 h-3.5" />; };

  return (
    <DealerLayout title="Marketplace">
      <div className="space-y-6" data-testid="dealer-marketplace">
        <div className="flex gap-2 mb-4">
          <button onClick={() => setTab("browse")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "browse" ? "bg-teal text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`} data-testid="tab-browse">
            <Package className="w-4 h-4 inline mr-1.5" />Browse Products
          </button>
          <button onClick={() => setTab("cart")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${tab === "cart" ? "bg-teal text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`} data-testid="tab-cart">
            <ShoppingCart className="w-4 h-4 inline mr-1.5" />Cart {cart.length > 0 && <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5">{cart.length}</span>}
          </button>
          <button onClick={() => setTab("orders")} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "orders" ? "bg-teal text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`} data-testid="tab-orders">
            <Clock className="w-4 h-4 inline mr-1.5" />My Orders ({orders.length})
          </button>
        </div>

        {tab === "browse" && (
          <div>
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="product-search" />
              </div>
              {activeFilterCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="text-slate-600"
                  data-testid="clear-filters-btn"
                >
                  <FilterX className="w-4 h-4 mr-1.5" />
                  Clear filters ({activeFilterCount})
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2 mb-4" data-testid="marketplace-filters">
              <FilterSelect
                value={categoryFilter}
                onChange={setCategoryFilter}
                options={filterOptions.categories}
                placeholder="All Categories"
                testid="filter-category"
              />
              <FilterSelect
                value={subcategoryFilter}
                onChange={setSubcategoryFilter}
                options={filterOptions.subcategories}
                placeholder="All Sub-Categories"
                testid="filter-subcategory"
              />
              <FilterSelect
                value={itemGroupFilter}
                onChange={setItemGroupFilter}
                options={filterOptions.itemGroups}
                placeholder="All Item Groups"
                testid="filter-item-group"
              />
              <FilterSelect
                value={brandFilter}
                onChange={setBrandFilter}
                options={filterOptions.brands}
                placeholder="All Brands"
                testid="filter-brand"
              />
              <FilterSelect
                value={conditionFilter}
                onChange={setConditionFilter}
                options={filterOptions.conditions}
                placeholder="All Conditions"
                testid="filter-condition"
              />
              <FilterSelect
                value={carrierFilter}
                onChange={setCarrierFilter}
                options={filterOptions.carriers}
                placeholder="All Carriers"
                testid="filter-carrier"
              />
              <FilterSelect
                value={compatModelFilter}
                onChange={setCompatModelFilter}
                options={filterOptions.compatModels}
                placeholder="All Compatible Models"
                testid="filter-compatible-model"
              />
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>
            ) : productGroups.length === 0 ? (
              <Card className="border-slate-200"><CardContent className="py-12 text-center"><Package className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No products found</p></CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {productGroups.map((g) => {
                  const activeId = activeVariantByGroup[g.group_id] || g.variants[0].id;
                  const product = g.variants.find((v) => v.id === activeId) || g.variants[0];
                  const restricted = isRepairProduct(product) && !repairApproved;
                  const showSwatches = g.variants.length > 1;
                  return (
                    <Card
                      key={g.key}
                      className="border-slate-200 hover:border-teal/30 transition-colors cursor-pointer"
                      onClick={() => setSelectedProduct(product)}
                      data-testid={`product-${product.id}`}
                    >
                      <CardContent className="p-4">
                        {product.image_url ? (
                          <div className="w-full h-40 bg-slate-50 rounded-lg overflow-hidden mb-3 flex items-center justify-center" data-testid={`product-image-${product.id}`}>
                            <img src={product.image_url} alt={product.name} className="w-full h-full object-contain" loading="lazy" />
                          </div>
                        ) : (
                          <div className="w-full h-40 bg-slate-50 rounded-lg overflow-hidden mb-3 flex items-center justify-center">
                            <Package className="w-10 h-10 text-slate-300" />
                          </div>
                        )}
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-teal" />
                          </div>
                          <span className={`px-2 py-0.5 text-xs rounded font-medium ${product.quantity_in_stock > 10 ? "bg-emerald-100 text-emerald-700" : product.quantity_in_stock > 0 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                            {product.quantity_in_stock > 0 ? `${product.quantity_in_stock} in stock` : "Out of Stock"}
                          </span>
                        </div>
                        <h3 className="font-heading font-semibold text-navy text-sm mb-1 line-clamp-2" data-testid={`product-name-${product.id}`}>{product.name}</h3>
                        <div className="flex items-center justify-between mb-3 gap-2">
                          <p className="font-bold text-teal text-lg" data-testid={`product-price-${product.id}`}>${product.retail_price?.toFixed(2)}</p>
                          {showSwatches && (
                            <div className="flex items-center gap-1.5" data-testid={`variant-swatches-${g.group_id}`} onClick={(e) => e.stopPropagation()}>
                              {g.variants.map((v) => {
                                const sel = v.id === product.id;
                                return (
                                  <button
                                    key={v.id}
                                    type="button"
                                    onClick={() => setActiveVariantByGroup((prev) => ({ ...prev, [g.group_id]: v.id }))}
                                    title={v.variant_name || "Variant"}
                                    className={`w-5 h-5 rounded-sm border transition-all ${sel ? "ring-2 ring-teal ring-offset-1 border-teal" : "border-slate-300 hover:border-slate-500"}`}
                                    style={{ backgroundColor: v.variant_color_hex || "#cbd5e1" }}
                                    data-testid={`swatch-${v.id}`}
                                  />
                                );
                              })}
                            </div>
                          )}
                        </div>
                        {restricted ? (
                          <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg p-2">
                            <Lock className="w-3.5 h-3.5" /> Repair approval required
                          </div>
                        ) : product.quantity_in_stock === 0 ? (
                          <Button
                            size="sm"
                            className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                            onClick={(e) => { e.stopPropagation(); handleNotifyMe(product); }}
                            disabled={product.notify_requested}
                            data-testid={`notify-me-${product.id}`}
                          >
                            <Bell className="w-3 h-3 mr-1" /> {product.notify_requested ? "You'll be notified" : "Notify Me"}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="w-full bg-teal hover:bg-teal/90 text-white"
                            onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                            data-testid={`add-to-cart-${product.id}`}
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add to Cart
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "cart" && (
          <Card className="border-slate-200">
            <CardHeader><CardTitle className="font-heading text-navy">Shopping Cart</CardTitle></CardHeader>
            <CardContent>
              {cart.length === 0 ? (
                <div className="text-center py-8"><ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">Your cart is empty</p></div>
              ) : (
                <div className="space-y-3">
                  {cart.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-lg p-3" data-testid={`cart-item-${idx}`}>
                      <div className="flex-1"><p className="text-sm font-medium text-navy">{item.product_name}</p><p className="text-xs text-slate-400">${item.unit_price?.toFixed(2)} each</p></div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQty(idx, -1)}><Minus className="w-3 h-3" /></Button>
                        <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQty(idx, 1)}><Plus className="w-3 h-3" /></Button>
                        <span className="w-20 text-right text-sm font-semibold text-navy">${(item.quantity * item.unit_price).toFixed(2)}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => removeFromCart(idx)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                  <div className="border-t pt-4 mt-4">
                    <Input placeholder="Order notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="mb-4" data-testid="order-notes" />
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-bold text-navy">Total: ${cartTotal.toFixed(2)}</p>
                      <Button className="bg-teal hover:bg-teal/90 text-white" onClick={submitOrder} disabled={submitting} data-testid="submit-order-btn">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Place Order
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === "orders" && (
          <div>
            <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="order-filters">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search order number..." value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} className="pl-10" data-testid="order-search" />
              </div>
              <div className="flex gap-2">
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <Input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} className="pl-10 w-40" data-testid="order-date-start" />
                </div>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <Input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} className="pl-10 w-40" data-testid="order-date-end" />
                </div>
              </div>
            </div>
            <div className="space-y-3" data-testid="orders-list">
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>
              ) : filteredOrders.length === 0 ? (
                <Card className="border-slate-200"><CardContent className="py-12 text-center"><ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">{orderSearch || dateStart || dateEnd ? "No orders matching your filters" : "No orders yet"}</p></CardContent></Card>
              ) : (
                filteredOrders.map((order) => (
                  <Card key={order.id} className="border-slate-200" data-testid={`order-card-${order.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row justify-between gap-3 mb-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-navy font-heading">{order.order_number}</p>
                            <span className={`px-2 py-0.5 text-xs rounded font-medium inline-flex items-center gap-1 ${statusColors[order.status] || "bg-slate-100 text-slate-600"}`}>
                              {statusIcon(order.status)} {order.status}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">{new Date(order.created_at).toLocaleString()}</p>
                          {order.tracking_number && <p className="text-xs text-blue-600 mt-1">Tracking: {order.tracking_number}</p>}
                        </div>
                        <p className="font-bold text-navy text-lg">${order.total?.toFixed(2)}</p>
                      </div>
                      <div className="space-y-2">
                        {order.items?.map((item, i) => (
                          <div key={i} className="bg-slate-50 rounded-md p-3">
                            <div className="flex justify-between items-start">
                              <div><p className="text-sm font-medium text-navy">{item.product_name}</p><p className="text-xs text-slate-400">Qty: {item.quantity} @ ${item.unit_price?.toFixed(2)}</p></div>
                              <p className="text-sm font-semibold text-navy">${(item.quantity * item.unit_price).toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Product Detail Modal */}
        <ProductDetailModal
          product={selectedProduct}
          open={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={addToCart}
          onNotifyMe={handleNotifyMe}
          repairApproved={repairApproved}
        />
      </div>
    </DealerLayout>
  );
}
